// Studio display names, where the market calls a studio something other than
// its Salesforce record name.
//
// Australia names its studios by city AND suburb — "Adelaide | Mile End",
// "Sydney | Alexandria" — because the suburb alone means nothing to somebody
// searching for a fitting in Adelaide. Salesforce holds only the suburb, which
// is what the widget was showing, so an Australian customer looking for Sydney
// saw "Alexandria" and had no way to know that was the one.
//
// This is a lookup rather than a data change because there are five Australian
// studios and the alternative is a Salesforce field that has to be populated
// and kept in step for every market. If the list grows, or another market wants
// the same, that trade flips and the name belongs in Salesforce with the rest
// of the store record — at which point this file is deleted rather than
// extended.
//
// Keyed on the exact Salesforce store name. An unknown name passes through
// untouched, so a new studio appears under its own name rather than vanishing
// or throwing.
//
// @see CC Australian testing round, 2026-08-24

const DISPLAY_NAMES: Record<string, Record<string, string>> = {
  au: {
    'Mile End': 'Adelaide | Mile End',
    'Eagle Farm': 'Brisbane | Eagle Farm',
    Heatherton: 'Melbourne South | Heatherton',
    'Hawthorn East': 'Melbourne Central | Hawthorn East',
    Alexandria: 'Sydney | Alexandria',
  },
};

/**
 * What to show the customer for this studio.
 *
 * Display only — never send this to Salesforce. Availability, Schedule and
 * Cancel all match on the record name, so the raw `name` remains the value
 * that travels.
 */
export function storeDisplayName(name: string, country: string | null | undefined): string {
  const code = (country ?? '').trim().toLowerCase();
  return DISPLAY_NAMES[code]?.[name.trim()] ?? name;
}

/**
 * Whether a query matches the studio, including the parts of the display name
 * Salesforce does not know about.
 *
 * Without this, typing "Sydney" in the Australian picker finds nothing:
 * Salesforce calls that studio "Alexandria", and the city the customer
 * searched for exists only in the display name.
 */
export function matchesStoreName(
  name: string,
  country: string | null | undefined,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return false;
  return storeDisplayName(name, country).toLowerCase().includes(q);
}
